import { inject, Injectable, signal } from '@angular/core';
import { AppKit, createAppKit } from '@reown/appkit';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { mainnet, sepolia } from 'viem/chains';
import { environment } from '../../../src/environments/environment';
import { createAppKitWalletButton } from '@reown/appkit-wallet-button';
import { config } from '../config/wagmi-config';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { reconnect } from 'wagmi/actions';
import { AuthSessionService } from './auth-session.service';
import { ApiService } from './api.service';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AppKitService {
  private modal: AppKit | null = null;
  wagmiAdapter!: WagmiAdapter;
  private wagmiConfig = config;
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);
  private readonly authSessionService = inject(AuthSessionService);
  private readonly apiService = inject(ApiService);
  private walletState = signal<{ account: string; chainId: number; connected: boolean } | null>(null);
  public appKitWalletButton: ReturnType<typeof createAppKitWalletButton> | null = null;
  private currentAccount: string | null = null;
  private readonly ready = signal(false);
  private readyResolve!: () => void;
  private readonly readyPromise = new Promise<void>((resolve) => {
    this.readyResolve = resolve;
  });
  private sessionExpiryHandled = false;
  private disconnectInProgress = false;

  constructor() {
    this.initializeAppKit();
  }

  private async initializeAppKit() {
    const projectId = environment.PROJECT_ID;
    const metadata = {
      name: 'app-Aqlx',
      description: 'AppKit Example',
      url: 'https://reown.com/appkit',
      icons: ['https://assets.reown.com/reown-profile-pic.png'],
    };

    const activeChain = environment.production ? mainnet : sepolia;


    this.wagmiAdapter = new WagmiAdapter({
      projectId: projectId,
      networks: [activeChain],
    });

    this.modal = createAppKit({
      adapters: [this.wagmiAdapter],
      networks: [activeChain],
      projectId,
      metadata,
      features: {
        analytics: true,
        email: false,
        socials: false,
      },
      allWallets: 'HIDE',
    });
    this.wagmiConfig = this.wagmiAdapter.wagmiConfig as any;

    try {
      await reconnect(this.wagmiConfig as any);
      console.log('Reconnect completed');
    } finally {
      this.setConfig(this.wagmiAdapter);
      this.ready.set(true);
      this.readyResolve();
    }

    this.appKitWalletButton = createAppKitWalletButton();
    this.listenModalEvents();
  }
    
  async listenModalEvents() {
    if (!this.modal) return;

    const eventSubscription = this.modal.subscribeEvents((event: any) => {
      this.updateWalletInfo();
    });

    const accountSubscription = this.modal.subscribeAccount((account: any) => {
      this.updateWalletInfo(account);
    });

    this.updateWalletInfo();
  }

    /**
   * Set configuration
   * @param config 
   */
  setConfig(config: WagmiAdapter) {
    this.wagmiAdapter = config;
  }

  /**
   * get config
   * @returns 
   */
  fetchConfig() {
    return this.wagmiAdapter.wagmiConfig;
  }

  async whenReady(): Promise<void> {
    if (this.ready()) {
      return;
    }

    await this.readyPromise;
  }

  getResolvedAccount(): string | null {
    const walletAccount = this.walletState()?.account;
    if (walletAccount) {
      return walletAccount;
    }

    return this.readStoredAccount();
  }

  async switchNetwork() {
    if (!this.modal) return;
    await this.modal.switchNetwork({
      chainId: config.chains,
    } as any);
    this.updateWalletInfo();
  }

  async updateWalletInfo(accountInfo?: any) {
    let account = accountInfo?.address;
    let isConnected = accountInfo?.isConnected;

    if (accountInfo === undefined || accountInfo === null || typeof accountInfo !== 'object') {
      const fullInfo = JSON.parse(localStorage.getItem('wagmi.store') || '{}');
      account = fullInfo?.state?.connections?.value?.[0]?.[1]?.accounts?.at(-1);
      isConnected = !!account;
    }

    const fullInfo = JSON.parse(localStorage.getItem('wagmi.store') || '{}');
    const chainId = fullInfo?.state?.chainId || 0;

    const wasConnected = !!this.currentAccount;
    const oldAccount = this.currentAccount;
    const newAccount = account || '';

    if (isConnected && account) {
      this.walletState.set({ account, chainId, connected: true });
      this.currentAccount = account;
    } else {
      this.walletState.set({ account: '', chainId: 0, connected: false });
      this.currentAccount = null;
    }

    const isNotOnLoginPage = this.router.url !== '/login' && this.router.url !== '/';
    if (this.disconnectInProgress) {
      return;
    }

    if (isNotOnLoginPage) {
      if (wasConnected && !isConnected) {
        // During a page refresh wagmi fires a transient isConnected=false event
        // while it is still reconnecting. At that moment wagmi.store still holds
        // the active account. We cross-check the store so we only log the user
        // out when the store also confirms no active connection (real disconnect).
        const storeInfo = JSON.parse(localStorage.getItem('wagmi.store') || '{}');
        const storeAccount = storeInfo?.state?.connections?.value?.[0]?.[1]?.accounts?.at(-1);
        if (!storeAccount) {
          console.log('Wallet disconnected, logging out...');
          this.disconnect();
        }
      } else if (wasConnected && isConnected && oldAccount && oldAccount.toLowerCase() !== newAccount.toLowerCase()) {
        console.log('Wallet account changed, logging out...');
        this.toastr.error('Wallet account changed. Please login again.');
        this.disconnect();
      }
    }
  }

  getWalletSignal() {
    return this.walletState.asReadonly();
  }

  public openConnectModal() {
    this.modal?.open({ view: 'Connect' });
  }

  public openNetworkModal() {
    this.modal?.open({ view: 'Networks' });
  }

  public getWalletInfo() {
    return this.modal?.getWalletInfo();
  }

  async disconnect() {
    if (this.disconnectInProgress) {
      return;
    }

    this.disconnectInProgress = true;
    try {
      // 1. Call backend logout API to clear session & CSRF cookies
      try {
        await firstValueFrom(this.apiService.adminLogout());
      } catch (apiError) {
        console.error('Logout API call failed, proceeding with local cleanup:', apiError);
      }

      // 2. Disconnect the wallet
      if (this.modal) {
        await this.modal.disconnect();
      }

      // 3. Clear all local auth data
      this.clearWalletStorage();
      this.authSessionService.clearSession();
      this.resetSessionExpiryHandling();
      this.walletState.set({ account: '', chainId: 0, connected: false });
      this.currentAccount = null;

      // 4. Redirect to login
      this.router.navigateByUrl('/login');
    } catch (error) {
      console.error('Disconnect failed:', error);
      this.toastr.error('Failed to disconnect wallet');
    } finally {
      this.disconnectInProgress = false;
    }
  }

  handleSessionExpired(): void {
    if (this.sessionExpiryHandled) {
      return;
    }

    this.sessionExpiryHandled = true;
    this.toastr.error('Session expired. Please login again.', 'Unauthorized (401)');
    void this.disconnect();
  }

  resetSessionExpiryHandling(): void {
    this.sessionExpiryHandled = false;
  }

  public triggerWalletUpdate() {
    this.updateWalletInfo();
  }

  /**
   * Signs a message using the connected MetaMask wallet via window.ethereum.
   * This avoids the wagmi "Connector not connected" error caused by the
   * WagmiAdapter managing its own internal connector state separately from
   * AppKit's modal connection state.
   */
  async signMessage(message: string): Promise<string> {
    const ethereum = (window as any).ethereum;
    if (!ethereum) {
      throw new Error('MetaMask is not installed or not accessible');
    }

    const account = this.getResolvedAccount();
    if (!account) {
      throw new Error('No wallet connected');
    }

    // personal_sign expects [message_hex, address]
    const msgHex = '0x' + Array.from(new TextEncoder().encode(message))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');

    const signature = await ethereum.request({
      method: 'personal_sign',
      params: [msgHex, account],
    });

    return signature as string;
  }

  private clearWalletStorage(): void {
    const removeValue = [
      'isLogin',
      'adminToken',
      'adminUser',
      'account',
      'accessToken',
      'refreshToken',
      'authWalletAddress',
      'reown.store',
      'wagmi.store',
      'wagmi.recentConnectorId',
      '@appkit/native_balance_cache',
      '@appkit/token_balance_cache',
      '@appkit/ens_cache',
      '@appkit/identity_cache',
      '@appkit/active_caip_network_id',
      '@appkit/active_namespace',
      '@appkit/connected_namespaces',
    ];

    removeValue.forEach((key) => localStorage.removeItem(key));
    sessionStorage.clear();
  }

  private readStoredAccount(): string | null {
    try {
      const fullInfo = JSON.parse(localStorage.getItem('wagmi.store') || '{}');
      return (
        fullInfo?.state?.connections?.value?.[0]?.[1]?.accounts?.at(-1) ||
        localStorage.getItem('account') ||
        null
      );
    } catch {
      return localStorage.getItem('account');
    }
  }
}
